<!--
  /dash/settings/sending — BYO sending providers (own SMTP email + own Twilio SMS number).

  Settings are saved through the dashboard Cloud Run service (never direct Firestore —
  secrets get encrypted server-side into the admin-only sendingcredentials collection).
  The fetch returns MASKED settings: hasPassword/hasAuthToken booleans + last-4 accountSid.
  Leaving a secret field blank on save means "keep the stored one".
-->
<template>
<div>
  <Pagesubtitle subTitle="Send invites and followups from your own email address and SMS number." />

  <div class="w-full block overflow-auto pb-4 relative" v-if="loading">
    <div class="text-center py-10">
      <p class="mb-2">Loading sending settings...</p>
      <div class="w-8 h-8 pt_loading inline-block"></div>
    </div>
  </div>

  <div v-else-if="form">

    <!-- ============================== EMAIL ============================== -->
    <vx-card class="w-full mb-6">
      <label class="vs-input--label block mb-2"><span class="font-medium text-xl">EMAIL SENDING</span></label>
      <p class="my-2">How should your email invites, followups and referral emails be sent?</p>

      <div class="my-4">
        <vs-radio vs-name="emailmode" v-model="form.email.mode" vs-value="platform" class="text-dark font-medium">Send with Feedback Link (default)</vs-radio>
        <p>Emails are sent by Feedback Link and count towards your monthly invite allowance.</p>
      </div>
      <div class="my-4" v-if="byoSendingAllowed">
        <vs-radio vs-name="emailmode" v-model="form.email.mode" vs-value="byo" class="text-dark font-medium">Send via your own email provider</vs-radio>
        <p>Emails are sent from your own address for better deliverability and branding. They count towards your monthly invite allowance like any other invite.</p>
      </div>

      <div v-if="byoSendingAllowed && form.email.mode === 'byo'" class="mt-6">
        <vs-divider/>

        <label class="vs-input--label block mb-2">Email provider</label>
        <v-select placeholder="Select provider..." class="w-full bg-white mb-4 md:w-1/2" style="z-index: 9000;" :searchable="false" :clearable="false" :options="presetOptions" label="name" :reduce="p => p.key" v-model="form.email.preset" @input="applyPreset" />

        <p class="mb-4 text-sm" v-if="presetHint">{{ presetHint }}</p>

        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2 mb-4">
            <vs-input label="SMTP host" v-model="form.email.host" class="w-full" :disabled="presetLocksHost" />
          </div>
          <div class="vx-col w-full md:w-1/4 mb-4">
            <vs-input label="Port" type="number" v-model.number="form.email.port" class="w-full" :disabled="presetLocksHost" />
          </div>
          <div class="vx-col w-full md:w-1/4 mb-4 pt-6">
            <vs-checkbox v-model="form.email.secure" :disabled="presetLocksHost">Use SSL (port 465)</vs-checkbox>
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2 mb-4">
            <vs-input :label="usernameLabel" v-model="form.email.username" class="w-full" :disabled="form.email.preset === 'sendgrid'" />
          </div>
          <div class="vx-col w-full md:w-1/2 mb-4">
            <vs-input :label="passwordLabel" type="password" v-model="form.email.password" class="w-full" :placeholder="form.email.hasPassword ? 'Leave blank to keep the saved one' : ''" />
          </div>
        </div>

        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2 mb-4">
            <vs-input label="From email address" v-model="form.email.fromEmail" class="w-full" />
          </div>
          <div class="vx-col w-full md:w-1/2 mb-4">
            <vs-input label="From name (optional, defaults to your business name)" v-model="form.email.fromName" class="w-full" />
          </div>
        </div>

        <vs-divider/>

        <!-- test send -->
        <label class="vs-input--label block mb-2">Send a test email</label>
        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2 mb-2">
            <vs-input v-model="testEmailTo" class="w-full" placeholder="you@yourbusiness.com" />
          </div>
          <div class="vx-col w-full md:w-1/2 mb-2">
            <vs-button :disabled="!canTestEmail" @click="sendTestEmail">{{ testingEmail ? 'Sending...' : 'Send test email' }}</vs-button>
          </div>
        </div>
        <p class="text-sm" v-if="emailTestBlockedReason">{{ emailTestBlockedReason }}</p>
        <p class="text-sm text-success font-medium" v-if="emailTestStatus === 'ok'">Test email sent — check the inbox of {{ testEmailTo }}.</p>
        <p class="text-sm text-danger" v-if="emailTestStatus === 'fail'">Test failed: {{ emailTestError }}</p>
        <p class="text-sm mt-1" v-if="form.email.lastTestAt && !emailTestStatus">Last test: {{ form.email.lastTestOk ? 'succeeded' : 'failed' }} ({{ formatTime(form.email.lastTestAt) }})</p>
      </div>
    </vx-card>

    <!-- ============================== SMS ============================== -->
    <vx-card class="w-full mb-6">
      <label class="vs-input--label block mb-2"><span class="font-medium text-xl">TEXT (SMS) SENDING</span></label>
      <p class="my-2">How should your text invites and followups be sent?</p>

      <div class="my-4">
        <vs-radio vs-name="textmode" v-model="form.text.mode" vs-value="platform" class="text-dark font-medium">Send with Feedback Link (default)</vs-radio>
        <p>Texts are sent by Feedback Link and use your text credits.</p>
      </div>
      <div class="my-4" v-if="byoSendingAllowed">
        <vs-radio vs-name="textmode" v-model="form.text.mode" vs-value="byo" class="text-dark font-medium">Send via your own Twilio account</vs-radio>
        <p>Texts are sent from your own number and <span class="font-medium">don't use text credits</span> — Twilio bills you directly. They still count towards your monthly invite allowance.</p>
      </div>

      <div v-if="byoSendingAllowed && form.text.mode === 'byo'" class="mt-6">
        <vs-divider/>

        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2 mb-4">
            <vs-input label="Twilio Account SID" v-model="form.text.accountSid" class="w-full" :placeholder="form.text.accountSidMasked ? 'Saved: ' + form.text.accountSidMasked + ' (leave blank to keep)' : 'AC...'" />
          </div>
          <div class="vx-col w-full md:w-1/2 mb-4">
            <vs-input label="Twilio Auth Token" type="password" v-model="form.text.authToken" class="w-full" :placeholder="form.text.hasAuthToken ? 'Leave blank to keep the saved one' : ''" />
          </div>
        </div>
        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2 mb-4">
            <vs-input label="From number (your Twilio number, e.g. +12025551234)" v-model="form.text.fromNumber" class="w-full" />
          </div>
        </div>
        <p class="text-sm mb-4">Find both on your <a href="https://console.twilio.com" target="_blank" rel="noopener">Twilio Console</a> home page. Note: Twilio <span class="font-medium">test</span> credentials will pass the test below but can't deliver real texts — use your live credentials.</p>

        <vs-divider/>

        <!-- test send -->
        <label class="vs-input--label block mb-2">Send a test text</label>
        <div class="vx-row">
          <div class="vx-col w-full md:w-1/2 mb-2">
            <vs-input v-model="testSmsTo" class="w-full" placeholder="+12025551234" />
          </div>
          <div class="vx-col w-full md:w-1/2 mb-2">
            <vs-button :disabled="!canTestSms" @click="sendTestSms">{{ testingSms ? 'Sending...' : 'Send test SMS' }}</vs-button>
          </div>
        </div>
        <p class="text-sm" v-if="smsTestBlockedReason">{{ smsTestBlockedReason }}</p>
        <p class="text-sm text-success font-medium" v-if="smsTestStatus === 'ok'">Test SMS accepted by Twilio — check {{ testSmsTo }}.</p>
        <p class="text-sm text-danger" v-if="smsTestStatus === 'fail'">Test failed: {{ smsTestError }}</p>
        <p class="text-sm mt-1" v-if="form.text.lastTestAt && !smsTestStatus">Last test: {{ form.text.lastTestOk ? 'succeeded' : 'failed' }} ({{ formatTime(form.text.lastTestAt) }})</p>
      </div>
    </vx-card>

  </div>

  <savebutton submitText="Save Sending Settings" :accept="save" :goodToGo="formReady" />
</div>
</template>


<script>
import Pagesubtitle from './components/ptcomponents/Pagesubtitle.vue'
import Savebutton from './components/ptcomponents/Savebutton.vue'
import vSelect from 'vue-select'

// preset -> SMTP connection prefill. SendGrid/Mailgun/Office365/Gmail lock the connection
// fields (they're fixed by the provider); SES keeps host editable (region-specific).
const PRESETS = {
  sendgrid: { name: 'SendGrid', host: 'smtp.sendgrid.net', port: 587, secure: false, username: 'apikey', lockHost: true,
    hint: 'Username is always "apikey" — paste your SendGrid API key as the password.' },
  mailgun: { name: 'Mailgun', host: 'smtp.mailgun.org', port: 587, secure: false, username: '', lockHost: true,
    hint: 'Use your Mailgun SMTP login (postmaster@yourdomain) and SMTP password.' },
  ses: { name: 'Amazon SES', host: 'email-smtp.us-east-1.amazonaws.com', port: 587, secure: false, username: '', lockHost: false,
    hint: 'Adjust the host to your SES region. Use your SMTP credentials (not your AWS login).' },
  office365: { name: 'Office 365 / Outlook', host: 'smtp.office365.com', port: 587, secure: false, username: '', lockHost: true,
    hint: 'Username is your full Office 365 email address.' },
  gmail: { name: 'Gmail / Google Workspace', host: 'smtp.gmail.com', port: 465, secure: true, username: '', lockHost: true,
    hint: 'Use an App Password (Google Account > Security > 2-Step Verification > App passwords) — your normal password won\'t work.' },
  custom: { name: 'Custom SMTP', host: '', port: 587, secure: false, username: '', lockHost: false, hint: '' }
}

export default {
  data() {
    return {
      loading: false,
      form: false,
      savedState: false,
      testEmailTo: '',
      testSmsTo: '',
      testingEmail: false,
      testingSms: false,
      emailTestStatus: false,
      emailTestError: '',
      smsTestStatus: false,
      smsTestError: ''
    }
  },
  components: {
    Pagesubtitle,
    Savebutton,
    'v-select': vSelect
  },
  computed: {
    //PRICING 2026: BYO sending (own SMTP / Twilio) is gated on the resolved plan's
    //limits.byoSending (false on Free). Backend enforces this too; this is the UI gate.
    byoSendingAllowed() {
      return !!this.$store.getters['dash/planResolved'].limits.byoSending
    },
    //hide the upgrade CTA for collaborators and agency-managed accounts
    upgradeAllowed() {
      if (this.$store.getters['dash/isCollaborator']) return false
      return this.$store.state.dash.viewData.business.upgradeCTAVisible !== false
    },
    presetOptions() {
      return Object.keys(PRESETS).map(key => ({ key, name: PRESETS[key].name }))
    },
    presetHint() {
      const p = PRESETS[this.form.email.preset]
      return p ? p.hint : ''
    },
    presetLocksHost() {
      const p = PRESETS[this.form.email.preset]
      return p ? p.lockHost : false
    },
    usernameLabel() {
      return this.form.email.preset === 'sendgrid' ? 'SMTP username (always "apikey")' : 'SMTP username'
    },
    passwordLabel() {
      return this.form.email.preset === 'sendgrid' ? 'SendGrid API key' : 'SMTP password'
    },
    isDirty() {
      return this.form && JSON.stringify(this.form) !== this.savedState
    },
    formReady() {
      return this.isDirty
    },
    //test buttons only make sense for a SAVED byo config — the backend rejects platform
    //tests, and testing unsaved edits would test the wrong thing
    emailTestBlockedReason() {
      if (!this.form || this.form.email.mode !== 'byo') return ''
      if (this.isDirty) return 'Save your settings first, then send the test.'
      if (!this.form.email.hasPassword) return 'Save your SMTP password first, then send the test.'
      return ''
    },
    canTestEmail() {
      return this.form && this.form.email.mode === 'byo' && !this.isDirty && this.form.email.hasPassword
        && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.testEmailTo.trim()) && !this.testingEmail
    },
    smsTestBlockedReason() {
      if (!this.form || this.form.text.mode !== 'byo') return ''
      if (this.isDirty) return 'Save your settings first, then send the test.'
      if (!this.form.text.hasAuthToken) return 'Save your Twilio auth token first, then send the test.'
      return ''
    },
    canTestSms() {
      return this.form && this.form.text.mode === 'byo' && !this.isDirty && this.form.text.hasAuthToken
        && /^\+[1-9]\d{1,14}$/.test(this.testSmsTo.trim()) && !this.testingSms
    }
  },
  methods: {
    formatTime(s) {
      return new Date(s * 1000).toLocaleString()
    },
    applyPreset(key) {
      const p = PRESETS[key]
      if (!p) return
      if (p.host !== '' || key === 'custom') this.form.email.host = p.host
      this.form.email.port = p.port
      this.form.email.secure = p.secure
      if (p.username !== '') this.form.email.username = p.username
      else if (key !== 'ses' && this.form.email.username === 'apikey') this.form.email.username = ''
    },
    notifyIssue(text) {
      this.$vs.notify({
        title: 'Sending Settings',
        text: text,
        color: 'warning',
        time: 20000,
        iconPack: 'feather',
        icon: 'icon-alert-circle'
      })
    },
    load() {
      this.loading = true
      this.$store.dispatch('dash/FETCH_SENDING_SETTINGS').then((res) => {
        const s = res.settings
        this.form = {
          email: {
            mode: s.email.mode,
            preset: s.email.preset,
            host: s.email.host,
            port: s.email.port,
            secure: s.email.secure,
            username: s.email.username,
            password: '',
            fromEmail: s.email.fromEmail,
            fromName: s.email.fromName,
            hasPassword: s.email.hasPassword,
            lastTestAt: s.email.lastTestAt,
            lastTestOk: s.email.lastTestOk
          },
          text: {
            mode: s.text.mode,
            accountSid: '',
            accountSidMasked: s.text.accountSidMasked,
            authToken: '',
            fromNumber: s.text.fromNumber,
            hasAuthToken: s.text.hasAuthToken,
            lastTestAt: s.text.lastTestAt,
            lastTestOk: s.text.lastTestOk
          }
        }
        if (!this.testEmailTo) this.testEmailTo = this.$store.state.auth.firebaseUser.email || ''
        //if the plan doesn't allow BYO sending, never surface (or let the user keep)
        //a 'byo' mode — fall back to platform sending. Backend enforces this regardless.
        if (!this.byoSendingAllowed) {
          if (this.form.email.mode === 'byo') this.form.email.mode = 'platform'
          if (this.form.text.mode === 'byo') this.form.text.mode = 'platform'
        }
        this.savedState = JSON.stringify(this.form)
        this.loading = false
      }).catch(() => {
        this.loading = false
        this.notifyIssue('Oops! We had an issue loading your sending settings. Please try again or get support at support@feedback.link')
      })
    },
    save() {
      //client-side pre-checks mirroring the backend validation, for friendlier messages
      if (this.form.email.mode === 'byo') {
        const e = this.form.email
        if (!e.host || !e.username) return this.notifyIssue('Please fill in your SMTP host and username.')
        if (!e.port || e.port < 1 || e.port > 65535) return this.notifyIssue('Please enter a valid SMTP port.')
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e.fromEmail).trim())) return this.notifyIssue('Please enter a valid "from" email address.')
        if (!e.password && !e.hasPassword) return this.notifyIssue('Please enter your SMTP password.')
      }
      if (this.form.text.mode === 'byo') {
        const t = this.form.text
        if (t.accountSid && !/^AC[0-9a-fA-F]{32}$/.test(t.accountSid.trim())) return this.notifyIssue('That doesn\'t look like a Twilio Account SID (starts with AC, 34 characters).')
        if (!t.accountSid && !t.accountSidMasked) return this.notifyIssue('Please enter your Twilio Account SID.')
        if (!/^\+[1-9]\d{1,14}$/.test(String(t.fromNumber).trim())) return this.notifyIssue('Please enter your Twilio number in international format, e.g. +12025551234.')
        if (!t.authToken && !t.hasAuthToken) return this.notifyIssue('Please enter your Twilio auth token.')
      }

      const payload = {
        email: {
          mode: this.form.email.mode,
          preset: this.form.email.preset,
          host: this.form.email.host,
          port: this.form.email.port,
          secure: this.form.email.secure,
          username: this.form.email.username,
          fromEmail: this.form.email.fromEmail,
          fromName: this.form.email.fromName,
          password: this.form.email.password
        },
        text: {
          mode: this.form.text.mode,
          accountSid: this.form.text.accountSid,
          fromNumber: this.form.text.fromNumber,
          authToken: this.form.text.authToken
        }
      }

      this.$store.dispatch('dash/UPDATE_SENDING_SETTINGS', payload).then(() => {
        this.emailTestStatus = false
        this.smsTestStatus = false
        this.$vs.notify({
          title: 'Sending Settings',
          text: 'Sending settings updated.',
          color: 'success',
          time: 8000,
          iconPack: 'feather',
          icon: 'icon-alert-circle'
        })
        this.load()
      }).catch((e) => {
        const code = e && e.response && e.response.data && e.response.data.error
        let text = 'Oops! We had an issue saving your sending settings. Please try again or get support at support@feedback.link'
        if (code === 'missingsecret') text = 'Please enter your password / auth token before switching to your own provider.'
        else if (code === 'invalidfromnumber') text = 'Please enter your Twilio number in international format, e.g. +12025551234.'
        else if (code === 'invalidaccountsid') text = 'That doesn\'t look like a Twilio Account SID (starts with AC, 34 characters).'
        else if (code === 'invalidfromemail') text = 'Please enter a valid "from" email address.'
        else if (code === 'invalidport') text = 'Please enter a valid SMTP port.'
        else if (code === 'invalidhost') text = 'Please fill in your SMTP host.'
        else if (code === 'invalidusername') text = 'Please fill in your SMTP username.'
        this.notifyIssue(text)
      })
    },
    sendTestEmail() {
      this.testingEmail = true
      this.emailTestStatus = false
      this.$store.dispatch('dash/SEND_TEST_EMAIL', { toEmail: this.testEmailTo.trim() }).then((res) => {
        this.testingEmail = false
        if (res.success) {
          this.emailTestStatus = 'ok'
          this.form.email.lastTestOk = true
        } else {
          this.emailTestStatus = 'fail'
          this.emailTestError = res.detail || 'unknown error'
          this.form.email.lastTestOk = false
        }
        this.savedState = JSON.stringify(this.form) //lastTestOk isn't a user edit
      }).catch(() => {
        this.testingEmail = false
        this.emailTestStatus = 'fail'
        this.emailTestError = 'request failed — please try again'
      })
    },
    sendTestSms() {
      this.testingSms = true
      this.smsTestStatus = false
      this.$store.dispatch('dash/SEND_TEST_SMS', { toNumber: this.testSmsTo.trim() }).then((res) => {
        this.testingSms = false
        if (res.success) {
          this.smsTestStatus = 'ok'
          this.form.text.lastTestOk = true
        } else {
          this.smsTestStatus = 'fail'
          this.smsTestError = res.detail || 'unknown error'
          this.form.text.lastTestOk = false
        }
        this.savedState = JSON.stringify(this.form)
      }).catch(() => {
        this.testingSms = false
        this.smsTestStatus = 'fail'
        this.smsTestError = 'request failed — please try again'
      })
    }
  },
  created() {
    this.load()
  }
}
</script>

<style lang="scss">
</style>
